import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { motion } from "framer-motion";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const SEVERITY_FILL = {
  low: "#22C55E",
  moderate: "#F59E0B",
  high: "#EA580C",
  severe: "#DC2626",
};

const HAZARD_EMOJI = {
  flood: "🌊",
  wildfire: "🔥",
  heatwave: "🌡️",
  cyclone: "🌀",
  landslide: "⛰️",
  drought: "🏜️",
  "air-quality": "💨",
};

export default function WorldMap() {
  const [reports, setReports] = useState([]);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    http.get("/civic-reports?limit=100").then(r => setReports((r.data.reports || []).filter(x => x.lat != null && x.lon != null)));
  }, []);

  return (
    <section data-testid="world-map-section" className="py-16 md:py-24 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/ Live hazard map</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">The world, right now.</h2>
        </div>
        <p className="md:col-span-6 md:col-start-7 self-end text-[color:var(--np-text-2)] text-lg">
          Every civic report plotted at its source. Hover to inspect. Colour maps to severity.
        </p>
      </div>

      <div className="relative bg-[color:var(--np-bg-2)] border border-black/10 overflow-hidden">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 165 }}
          style={{ width: "100%", height: "auto" }}
          data-testid="world-map-svg"
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#FBE5C4"
                  stroke="#E4572E"
                  strokeWidth={0.35}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#F5B342", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
          {reports.map((r) => (
            <Marker key={r.report_id} coordinates={[r.lon, r.lat]}>
              <g onMouseEnter={() => setHover(r)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }}>
                <circle r={r.severity === "severe" ? 8 : r.severity === "high" ? 6 : 5} fill={SEVERITY_FILL[r.severity] || "#EA580C"} fillOpacity={0.35} />
                <circle r={r.severity === "severe" ? 4 : 3} fill={SEVERITY_FILL[r.severity] || "#EA580C"} stroke="#241209" strokeWidth={0.6} />
              </g>
            </Marker>
          ))}
        </ComposableMap>

        {hover && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 right-4 max-w-xs glass p-4"
            data-testid="map-hover-card"
          >
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)] mb-1">
              {HAZARD_EMOJI[hover.hazard] || "⚠️"} {hover.hazard.replace("-", " ")} · {hover.severity}
            </div>
            <div className="font-serif-display text-2xl leading-tight tracking-tight">{hover.location}</div>
            <p className="text-sm text-[color:var(--np-text-2)] mt-2">{hover.description}</p>
            <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mt-2">— {hover.reporter_name}</div>
          </motion.div>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 glass px-3 py-2 flex items-center gap-4 font-mono-tech text-[9px] tracking-[0.25em] uppercase">
          {Object.entries(SEVERITY_FILL).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: v }} />{k}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
